import { getOidcClient } from '../auth/oidc-client';

/**
 * Fetch data from API
 * @param {string} url
 * @param {object} options
 * @param {boolean} requireAuth When true a bearer token will be added to the request
 * and automatic signin redirects will be applied
 */
export async function secureFetch(url, options = {}, requireAuth = false) {
    const oidcClient = getOidcClient();
    if (requireAuth && !oidcClient) {
        throw new Error('Authentication is disabled for this OEM.');
    }
    const user = requireAuth ? await oidcClient.getUser() : null;

    // If authentication is required check if user is valid
    if (requireAuth && (!user || user.expired)){
        await oidcClient.signinRedirect({ state: { returnUrl: window.location.href } });
        throw new Error('User session expired!');
    }

    const headers = {
        ...(options.headers || {}),
    }

    // Add bearer token to header if required
    if (requireAuth && user?.access_token) {
        headers.Authorization = `Bearer ${user.access_token}`;
    }

    // Create packaged fetch request and wait for response
    const result = await fetch(url, {
        ...options,
        headers,
    });

    if (result.status === 401) {
        // If auth is required, try to log in user
        if (requireAuth){
            await oidcClient.removeUser();
            await oidcClient.signinRedirect({ state: { returnUrl: window.location.href } });
        }
        throw new Error('Unauthorized access!');
    }

    return result;
}

/**
 * Fetch data from API
 * @param {string} url
 * @param {object} options
 * @param {boolean} requireAuth When true a bearer token will be added to the request
 * and automatic signin redirects will be applied
 * @param {boolean} ignoreAbortError When true, ignore AbortError and return null
 * @returns {Promise<{result: Response}|{error: Error}|null>}
 * 1. If fetch succeeds, return { result: Response }
 * 2. If fetch fails with AbortError or signal is aborted, return null
 * 3. Otherwise, return { error: Error }
 */
export async function trySecureFetch(url, options = {}, requireAuth = false, ignoreAbortError = true) {
    const signal = options.signal;

    try {
        const result = await secureFetch(url, options, requireAuth);
        return { result };
    } catch (error) {
        if (ignoreAbortError && (error.name === 'AbortError' || signal?.aborted)) return null;
        return { error };
    }
}

/**
 * Fetch data from API
 * @param {string} url
 * @param {object} options
 * @param {boolean} requireAuth When true a bearer token will be added to the request
 * and automatic signin redirects will be applied
 * @param {boolean} ignoreAbortError When true, ignore AbortError and return null
 * @param {function} onBeforeFetch Callback function to be called before the fetch
 * @param {function} onAfterFetch Callback function to be called after the fetch
 * @param {function} onError Callback function to be called if an error occurs during the fetch
 * @returns {Promise<Response|null>}
 */
export async function trySecureFetchWithHooks(
    url,
    options = {},
    requireAuth = false,
    ignoreAbortError = true,
    {onBeforeFetch, onAfterFetch, onError}
) {
    const signal = options.signal;

    try {
        onBeforeFetch?.();
        return await secureFetch(url, options, requireAuth);
    } catch (error) {
        if (ignoreAbortError && (error.name === 'AbortError' || signal?.aborted)) return null;
        onError?.(error);
        return null;
    } finally {
        onAfterFetch?.();
    }
}